import { monkMovementBonus } from '../monk.mjs';
import { hasRangerFeature, rovingSpeedBonus } from '../ranger.mjs';
import { hasDraconicFlight } from '../speciesTraits.mjs';
import { normalizeEquipmentMap } from '../equipmentSlots.mjs';
import { hasSubclassFeature } from '../subclasses/index.mjs';
import { normalizeMovement, getEffectiveWalkSpeed } from '../../../components/Zombies/utils/movementCore.mjs';
const toFiniteNumberOrNull = value => value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);
const toFiniteNumberOrZero = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const isExplicitlyUnowned = entry => !!entry && typeof entry === 'object' && !Array.isArray(entry) && entry.owned === false;
const normalizeAccessoryCollection = character => Array.isArray(character?.accessories) ? character.accessories : Array.isArray(character?.accessory) ? character.accessory : [];
const getBarbarianLevel = character => (character?.occupation || []).reduce((total, entry) => String(entry.Name ?? entry.Occupation ?? entry.name ?? entry.occupation ?? '').trim().toLowerCase() === 'barbarian' ? total + Math.max(0, toFiniteNumberOrZero(entry.Level ?? entry.level)) : total, 0);
// Legacy feat speed follows 2 labels, 18 skills, 6 ability bonuses and 2 numeric bonuses.
const collectFeatNumericBonuses = feats => ({ speed: (Array.isArray(feats) ? feats : []).reduce((sum, feat) => sum + toFiniteNumberOrZero(Array.isArray(feat) ? feat[28] : feat?.speed), 0) });


const collectMovementSpeedBonus = (value) => {
  if (!value || isExplicitlyUnowned(value)) {
    return 0;
  }
  if (Array.isArray(value)) {
    return value.reduce((total, entry) => total + collectMovementSpeedBonus(entry), 0);
  }
  if (typeof value !== 'object') {
    return 0;
  }

  const directBonus =
    toFiniteNumberOrZero(value.speedBonus) +
    toFiniteNumberOrZero(value.movementSpeedBonus) +
    toFiniteNumberOrZero(value.walkingSpeedBonus);

  return (
    directBonus +
    collectMovementSpeedBonus(value.numericBonuses) +
    collectMovementSpeedBonus(value.bonuses) +
    collectMovementSpeedBonus(value.effects)
  );
};

export const isHeavyArmorItem = (item) => {
  if (!item || isExplicitlyUnowned(item)) {
    return false;
  }

  if (Array.isArray(item)) {
    const category = String(item[3] ?? '').toLowerCase();
    return category === 'heavy' || category.includes('heavy armor');
  }

  if (typeof item !== 'object') {
    return false;
  }

  const armorClassification = String(
    item.category ?? item.type ?? item.armorType ?? ''
  ).toLowerCase();

  return armorClassification === 'heavy' || armorClassification.includes('heavy armor');
};

const calculateClassMovementSpeedBonus = (character, equipmentEntries) => {
  const hasHeavyArmorEquipped = equipmentEntries.some((item) => isHeavyArmorItem(item));
  const fastMovement = getBarbarianLevel(character) >= 5 && !hasHeavyArmorEquipped ? 10 : 0;
  return fastMovement + rovingSpeedBonus(character, hasHeavyArmorEquipped);
};

export const calculateCharacterBaseMovementSpeed = (character, overrides = {}) => {
  if (!character || typeof character !== 'object') {
    return null;
  }

  const baseSpeed =
    toFiniteNumberOrNull(overrides.baseSpeed) ??
    toFiniteNumberOrNull(character.speed) ??
    toFiniteNumberOrNull(character?.race?.speed) ??
    null;
  if (baseSpeed === null) return null;

  const featBonuses = collectFeatNumericBonuses(character?.feat).speed;
  const normalizedEquipment = normalizeEquipmentMap(character?.equipment);
  const equipmentEntries = Object.values(normalizedEquipment || {}).filter(Boolean);
  const accessoryEntries = normalizeAccessoryCollection(character);
  const classMovementSpeedBonus = calculateClassMovementSpeedBonus(character, equipmentEntries) + monkMovementBonus(character);

  const total =
    baseSpeed +
    featBonuses +
    classMovementSpeedBonus +
    collectMovementSpeedBonus(character?.race) +
    collectMovementSpeedBonus(character?.classState) +
    collectMovementSpeedBonus(character?.features) +
    collectMovementSpeedBonus(character?.item) +
    collectMovementSpeedBonus(character?.items) +
    collectMovementSpeedBonus(character?.armor) +
    collectMovementSpeedBonus(equipmentEntries) +
    collectMovementSpeedBonus(accessoryEntries) +
    collectMovementSpeedBonus(character?.conditions) +
    collectMovementSpeedBonus(character?.statusConditions) +
    collectMovementSpeedBonus(character?.miscBonuses) +
    collectMovementSpeedBonus(character?.bonuses);

  return Math.max(0, Math.trunc(Number.isFinite(total) ? total : 0));
};


export const getMovementProfile = (actor, combat = {}, combatantId, { enemy = actor?.entityType === 'enemy', baseMovement } = {}) => {
  // Wild Shape: the Beast's Speeds replace the character's, and merged equipment adds nothing.
  const beastSpeed = actor?.classState?.druid?.wildShape?.form?.speed;
  const raw = beastSpeed ?? baseMovement ?? actor?.movement ?? actor?.speed ?? actor?.walkSpeed ?? (enemy ? actor?.movementSpeed ?? actor?.walkingSpeed : actor?.race?.speed);
  const base = normalizeMovement(raw);
  const walk = enemy ? (raw == null || raw === '' ? null : base.walk ?? 0) : calculateCharacterBaseMovementSpeed(beastSpeed ? { ...actor, equipment: {}, item: [], items: [], armor: [], accessories: [], accessory: [] } : actor, { baseSpeed: base.walk ?? (raw === undefined || raw === null || raw === '' ? null : 0) });
  const bonus = walk === null ? 0 : walk - (base.walk ?? 0);
  const speeds = Object.fromEntries(['walk','burrow','climb','fly','swim'].map(mode => {
    const value = mode === 'walk' ? walk : base[mode] === null ? null : Math.max(0, base[mode] + bonus);
    return [mode, value === null ? null : getEffectiveWalkSpeed(value, combat, combatantId)];
  }));
  if (!enemy && hasSubclassFeature(actor, 'rogue:thief:second-story-work')) speeds.climb = Math.max(speeds.climb || 0, speeds.walk || 0);
  // Roving (Ranger 6): a Climb Speed and a Swim Speed equal to its Speed.
  if (!enemy && hasRangerFeature(actor, 'roving')) { speeds.climb = Math.max(speeds.climb || 0, speeds.walk || 0); speeds.swim = Math.max(speeds.swim || 0, speeds.walk || 0); }
  // Draconic Flight (Dragonborn 5): a Fly Speed equal to its Speed while the wings last.
  // (Checked here rather than through speciesTraits.mjs: one more import root races the server's ESM loading.)
  if ((combat?.activeEffects || []).some(effect => effect?.definitionId === 'species:draconic-flight' && String(effect.targetCombatantId) === String(combatantId))) speeds.fly = Math.max(speeds.fly || 0, speeds.walk || 0);
  return { speeds, hover: base.hover === true, resolved: walk !== null, prone: (combat.activeEffects || []).some(effect => effect.definitionId === 'condition:prone' && String(effect.targetCombatantId) === String(combatantId)) };
};
